const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const SEARCH_RADIUS_METERS = 120;
const buildingCache = new Map();

const getBoundingBox = (latitude, longitude) => {
  const latitudeDelta = SEARCH_RADIUS_METERS / 111320;
  const longitudeDelta =
    SEARCH_RADIUS_METERS / (111320 * Math.max(Math.cos((latitude * Math.PI) / 180), 0.2));

  return [
    latitude - latitudeDelta,
    longitude - longitudeDelta,
    latitude + latitudeDelta,
    longitude + longitudeDelta,
  ];
};

const normalizeGeometry = (geometry) =>
  (geometry || [])
    .map((point) => [Number(point.lon), Number(point.lat)])
    .filter(([longitude, latitude]) => Number.isFinite(longitude) && Number.isFinite(latitude));

const isClosedLine = (points) => {
  if (points.length < 4) return false;
  const first = points[0];
  const last = points[points.length - 1];
  return Math.abs(first[0] - last[0]) < 1e-8 && Math.abs(first[1] - last[1]) < 1e-8;
};

const extractBuildingLines = (elements) =>
  elements.flatMap((element) => {
    if (element.type === 'way') {
      const line = normalizeGeometry(element.geometry);
      return line.length >= 2
        ? [{ id: `way-${element.id}`, points: line, isClosed: isClosedLine(line) }]
        : [];
    }

    if (element.type === 'relation') {
      return (element.members || [])
        .filter((member) => member.type === 'way' && member.role !== 'inner')
        .map((member, index) => ({
          id: `relation-${element.id}-${member.ref || index}`,
          points: normalizeGeometry(member.geometry),
        }))
        .filter((line) => line.points.length >= 2)
        .map((line) => ({ ...line, isClosed: isClosedLine(line.points) }));
    }

    return [];
  });

export const loadNearbyBuildingLines = async (latitude, longitude, signal) => {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return [];

  const cacheKey = `${latitude.toFixed(4)},${longitude.toFixed(4)}`;
  if (buildingCache.has(cacheKey)) return buildingCache.get(cacheKey);

  const bbox = getBoundingBox(latitude, longitude).join(',');
  const query = `
    [out:json][timeout:15];
    (
      way["building"](${bbox});
      relation["building"](${bbox});
    );
    out geom;
  `;
  const response = await fetch(OVERPASS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
    },
    body: new URLSearchParams({ data: query }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Overpass request failed (${response.status})`);
  }

  const data = await response.json();
  const buildingLines = extractBuildingLines(data.elements || []);
  buildingCache.set(cacheKey, buildingLines);
  return buildingLines;
};

const toLocalMeters = ([longitude, latitude], referenceLatitude) => {
  const longitudeScale = 111320 * Math.cos((referenceLatitude * Math.PI) / 180);
  return [longitude * longitudeScale, latitude * 111320];
};

export const findBuildingSnapPoint = (point, buildingLines, maxDistanceMeters = 3) => {
  const referenceLatitude = point[1];
  const [pointX, pointY] = toLocalMeters(point, referenceLatitude);
  let nearest = null;

  buildingLines.forEach((line) => {
    for (let index = 0; index < line.points.length - 1; index += 1) {
      const start = line.points[index];
      const end = line.points[index + 1];
      const [startX, startY] = toLocalMeters(start, referenceLatitude);
      const [endX, endY] = toLocalMeters(end, referenceLatitude);
      const deltaX = endX - startX;
      const deltaY = endY - startY;
      const lengthSquared = deltaX * deltaX + deltaY * deltaY;
      const projection =
        lengthSquared === 0
          ? 0
          : Math.max(
              0,
              Math.min(
                1,
                ((pointX - startX) * deltaX + (pointY - startY) * deltaY) / lengthSquared,
              ),
            );
      const snappedX = startX + projection * deltaX;
      const snappedY = startY + projection * deltaY;
      const distance = Math.hypot(pointX - snappedX, pointY - snappedY);

      if (!nearest || distance < nearest.distance) {
        nearest = {
          distance,
          point: [
            start[0] + projection * (end[0] - start[0]),
            start[1] + projection * (end[1] - start[1]),
          ],
        };
      }
    }
  });

  return nearest && nearest.distance <= maxDistanceMeters ? nearest : null;
};
